import { bool, func, object, shape, string } from 'prop-types';
import React, { Component } from 'react';
import _cloneDeep from 'lodash/cloneDeep';
import _get from 'lodash/get';
import _find from 'lodash/find';
import _isEmpty from 'lodash/isEmpty';
import styled from 'styled-components';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import _isEqual from 'lodash/isEqual';

import { AlertIcon } from 'ravenjs/lib';
import { COLORS } from 'ravenjs/constants/colors';
import ListView from 'ravenjs/components/ListView';
import { extractErrorMessage } from 'ravenjs/utils/error';
import { get } from 'ravenjs/utils/lodash';
import Typography from 'ravenjs/lib/Typography';
import { callFunc } from 'ravenjs/utils/actions';
import Row from 'ravenjs/lib/Row';
import Checkbox from 'ravenjs/lib/CheckboxNew';

import PackageWarning from 'components/PackageWarning';
import { CheckboxEmptyIcon, CheckboxFilledIcon } from 'components/Icons';
import { CONFIGURATION_TYPE } from 'constants/configuration';
import MESSAGES from 'constants/messages';
import { MODALS } from 'constants/modals';
import * as adminActions from 'modules/admin/actions';
import { actions as uiActions, selectors as uiSelectors } from 'modules/ui';
import {
    filterSystemSkusFromList,
    generateSKULicenseName,
    getRankedLicenseName,
    sortSkuList,
} from 'utils/skus';

import AddSkusPackageListSchema from './AddSkusPackageListSchema.json';
import Modal from '../Modal';

const AddSkusModalStyled = styled.div``;

class AddSkusModalPackaging extends Component {
    static propTypes = {
        closeModal: func.isRequired,
        fetchMasterConfigSkusForPartner: func.isRequired,
        fetchSkusByConfigurationId: func.isRequired,
        fetchCompany: func.isRequired,
        fetchPartner: func.isRequired,
        isLoading: func.isRequired,
        openModal: func.isRequired,
        modal: shape({
            data: object,
            open: bool,
        }),
        configLicenseName: string,
    };

    static defaultProps = {
        modal: {
            open: false,
            data: null,
        },
        configLicenseName: '',
    };

    static defaultState = {
        newAddedSkuIds: [],
        selectedSkuIds: [],
        saveSelectedSkusId: [],
        selectAll: false,
        skusList: [],
        skuSponsorsList: [],
        partnerPackageName: 'Basic',
        newAddedSkus: [],
        open: false,
    };

    constructor(props) {
        super(props);

        this.listRef = React.createRef();
        this.state = {
            ...AddSkusModalPackaging.defaultState,
        };
    }

    static getDerivedStateFromProps(nextProps, state) {
        const { open } = state;
        if (nextProps.modal.open && !open) {
            const selectedSkusList = _get(nextProps, 'modal.data.selectedSkusList', []);
            const skusList = _get(nextProps, 'modal.data.skusList', []);
            const selectedSkuIds = selectedSkusList.map(({ id }) => id);
            return {
                selectedSkuIds,
                saveSelectedSkusId: selectedSkuIds,
                selectAll: selectedSkuIds.length === skusList.length,
                open: nextProps.modal.open,
                skusList: skusList.map(sku => {
                    if (selectedSkuIds.indexOf(sku.id) !== -1) {
                        return {
                            ...sku,
                            disabled: true,
                        };
                    }

                    return sku;
                }),
                workflowStep: {
                    id: 'step_1',
                },
                validation: {
                    confirmSelection: true,
                },
            };
        }
        if (!nextProps.modal.open && open) {
            return {
                open: nextProps.modal.open,
            };
        }

        return null;
    }

    getSnapshotBeforeUpdate(prevProps, prevState) {
        if (this.listRef?.current) {
            const list = this.listRef.current;
            return list.scrollHeight - list.scrollTop;
        }
        return null;
    }

    componentDidUpdate(prevProps, prevState, snapshot) {
        if (snapshot !== null && this.listRef?.current) {
            const list = this.listRef.current;
            list.scrollTop = list.scrollHeight - snapshot;
        }
    }

    closeModal = () => {
        const { closeModal } = this.props;
        this.setState(
            {
                ...AddSkusModalPackaging.defaultState,
            },
            () => {
                closeModal(MODALS.ADD_SKUS_PACKAGING);
            }
        );
    };

    getActions = () => {
        const {
            workflowStep: { id, handleOnConfirmSelection } = {},
            validation,
            newAddedSkuIds,
        } = this.state;
        const confirmSelection = _get(validation, 'confirmSelection');

        const actions = {
            step_1: [
                {
                    onClick: this.closeModal,
                    text: 'Cancel',
                    color: 'cancelButton',
                },
                {
                    onClick: this.handleOnAdd,
                    text: MESSAGES.MODAL.CONFIGURATION.ACTIONS.CONTINUE,
                    color: 'primary',
                    disabled: _isEmpty(newAddedSkuIds),
                },
            ],
            step_2: [
                {
                    onClick: () => {
                        this.setState({
                            workflowStep: {
                                id: 'step_1',
                            },
                        });
                    },
                    text: 'Cancel',
                    color: 'cancelButton',
                },
                {
                    onClick: () => {
                        callFunc(handleOnConfirmSelection);
                        this.closeModal();
                    },
                    text: MESSAGES.MODAL.ADD_SKU.CONFIRM_SELECTIONS,
                    color: 'primary',
                    disabled: !confirmSelection,
                },
            ],
        };
        return actions[id];
    };

    getModalProps = () => {
        const { modal } = this.props;
        const { workflowStep } = this.state;
        const open = _get(modal, 'open', false);

        return {
            actions: this.getActions(),
            content: this.renderContent(),
            id: MODALS.ADD_SKUS_PACKAGING,
            modalBodyProps: {
                padding: '15px',
            },
            ModalProps: {
                ContentProps: {
                    style: {
                        maxWidth: '55rem',
                        margin: '1rem',
                    },
                },
            },
            open,
            title:
                workflowStep?.id === 'step_1'
                    ? MESSAGES.MODAL.ADD_SKU.TITLE
                    : MESSAGES.MODAL.ADD_SKU.CONFIRM_SELECTIONS,
            modalBodyRef: this.listRef,
        };
    };

    getDependentSkuName = sku => (sku.learnPackageId ? 'Learn Package' : sku.name);

    checkAddSkuValidation = async selectedSkus => {
        const { fetchPartner, fetchCompany, isLoading, modal, openModal } = this.props;
        const { skusList } = this.state;
        const configurationType = _get(modal, 'data.configurationType');
        let masterConfigurationSkusList = [];
        let assignedConfigurationSkusList = [];
        const selectedSkusIds = selectedSkus.map(({ id }) => id);
        const dependendencySKUs = selectedSkus.filter(sku => {
            let dependencyResolved = false;
            const skuDependentSkuIds = get(sku, 'skuDependentSkuIds', []);

            if (
                !_isEmpty(skuDependentSkuIds) &&
                Array.isArray(skuDependentSkuIds[0]) &&
                Array.isArray(skuDependentSkuIds[1])
            ) {
                const dependencyResolvedList = [];
                if (_isEmpty(skuDependentSkuIds)) {
                    return false;
                }
                skuDependentSkuIds.forEach(list => {
                    list.forEach(id => {
                        if (selectedSkusIds.indexOf(id) !== -1) {
                            dependencyResolvedList.push(true);
                        }
                    });
                });

                dependencyResolved = dependencyResolvedList.length === skuDependentSkuIds.length;
            } else {
                const dependentSkuIds = get(sku, 'dependentSkuIds', []);
                if (_isEmpty(dependentSkuIds)) {
                    return false;
                }
                dependentSkuIds.forEach(id => {
                    if (selectedSkusIds.indexOf(id) !== -1) {
                        dependencyResolved = true;
                    }
                });
            }

            return !dependencyResolved;
        });

        if (dependendencySKUs.length === 0) {
            return true;
        } else {
            let dependentSkuAdded = true;
            if (configurationType === CONFIGURATION_TYPE.ADD_ON) {
                const partnerId = _get(modal, 'data.partnerId');
                const companyId = _get(modal, 'data.companyId');
                isLoading(true, MESSAGES.LOADING.FETCH_CONFIGURATION);
                const partnerInfo = await fetchPartner(partnerId);
                const companyInfo = await fetchCompany(companyId);
                masterConfigurationSkusList = _get(partnerInfo, 'skus');
                assignedConfigurationSkusList = _get(companyInfo, 'skus');
                isLoading(false);
            }

            const dependencySkuNames = [];

            dependendencySKUs.forEach(sku => {
                let dependentSkuIds = [];
                const skuDependentSkuIds = get(sku, 'skuDependentSkuIds', []);

                if (
                    !_isEmpty(skuDependentSkuIds) &&
                    Array.isArray(skuDependentSkuIds[0]) &&
                    Array.isArray(skuDependentSkuIds[1])
                ) {
                    skuDependentSkuIds.forEach((list, key) => {
                        let found = false;

                        list.forEach(id => {
                            if (selectedSkusIds.indexOf(id) !== -1 && !found) {
                                found = true;
                            }
                        });

                        if (!found) {
                            dependentSkuIds[key] = list;
                        }
                    });

                    dependentSkuIds.forEach((list, key) => {
                        const listSkuNames = [];
                        list.forEach(id => {
                            let dependencySku = _find(skusList, { id });
                            if (
                                _isEmpty(dependencySku) &&
                                configurationType === CONFIGURATION_TYPE.ADD_ON
                            ) {
                                const assignedSkusList = _find(assignedConfigurationSkusList, {
                                    id,
                                });
                                if (_isEmpty(assignedSkusList)) {
                                    dependencySku = _find(masterConfigurationSkusList, { id });
                                }
                            }

                            if (!_isEmpty(dependencySku)) {
                                const SkuName = get(dependencySku, 'name');
                                listSkuNames.push(SkuName);
                            }
                        });

                        if (!_isEmpty(listSkuNames)) {
                            dependencySkuNames[key] = listSkuNames.join(' or ');
                        }
                    });
                } else {
                    // Getting the dependent SKU ids for the SKU
                    dependentSkuIds = get(sku, 'dependentSkuIds', []);
                    // Looking for any one SKU in the to resolve the dependency
                    dependentSkuIds.forEach(id => {
                        // Checking whether the dependency SKU is available in the current
                        // configuration SKU's list
                        let dependencySku = _find(skusList, { id });

                        // If the current configuration type is ADD-ON, then
                        // the SKU dependency will be also be considered if,
                        // - the dependency/parent SKU is available in
                        //    company's alternate configuration, or
                        // - the dependency/parent SKU is available in
                        //   the partner's master configuration
                        if (
                            _isEmpty(dependencySku) &&
                            configurationType === CONFIGURATION_TYPE.ADD_ON
                        ) {
                            dependencySku = _find(assignedConfigurationSkusList, { id });
                            if (_isEmpty(dependencySku)) {
                                dependencySku = _find(masterConfigurationSkusList, { id });
                            }
                        }

                        if (!_isEmpty(dependencySku)) {
                            dependentSkuAdded = true;
                        }
                    });
                }

                if (!dependentSkuAdded) {
                    openModal(MODALS.DEPENDENT_SKU_WARNING, {
                        sku: this.getDependentSkuName(sku),
                        dependencySKUs: dependencySkuNames,
                    });
                }
            });
            return dependentSkuAdded;
        }
    };

    rearrangeSkus = list => {
        const skusList = [];
        list.forEach(({ companyName, configurationName, skus = [] }) => {
            skus.forEach(({ id, name }) => {
                skusList.push({
                    companyName,
                    configurationName,
                    name,
                    skuId: id,
                });
            });
        });
        return skusList;
    };

    updateValidation = () => {
        const { validation } = this.state;
        const confirmSelection = _get(validation, 'confirmSelection');
        this.setState({
            validation: { confirmSelection: !confirmSelection },
        });
    };

    handleOnAdd = async () => {
        const {
            fetchSkusByConfigurationId,
            isLoading,
            openModal,
            modal,
            configLicenseName,
        } = this.props;
        const {
            newAddedSkuIds,
            selectedSkuIds,
            skusList,
            skuSponsorsList,
            validation: { confirmSelection } = {},
        } = this.state;
        const onAddSku = _get(modal, 'data.onAddSku');
        const configurationType = _get(modal, 'data.configurationType');
        const configurationId = _get(modal, 'data.configurationId');
        const ownerLicensesIds = _get(modal, 'data.ownerLicensesIds');
        const licenseList = _get(modal, 'data.licenseList', []);
        const configurationLicenses = _get(modal, 'data.configurationLicenses');
        const isConfigAssigned = _get(modal, 'data.isConfigAssigned');

        const selectedSkus = [];
        skusList.forEach(sku => {
            if (selectedSkuIds.indexOf(sku.id) !== -1) {
                let selectedSKU = { ...sku };

                const skuSponsor = skuSponsorsList.find(({ id }) => id === sku.id);

                if (skuSponsor) {
                    selectedSKU = {
                        ...selectedSKU,
                        sponsor: skuSponsor.sponsor.value,
                        sponsorBy: skuSponsor.sponsor.label,
                    };
                }

                if (newAddedSkuIds.includes(sku.id)) {
                    selectedSKU.isNew = true;
                }

                delete selectedSKU.disabled;

                selectedSkus.push(selectedSKU);
            }
        });

        const configurationLicensesTobe = getRankedLicenseName(
            selectedSkus,
            ownerLicensesIds,
            licenseList
        );
        const hasPackageChange =
            isConfigAssigned &&
            configurationType === CONFIGURATION_TYPE.ALTERNATE &&
            !_isEqual(configurationLicenses.sort(), configurationLicensesTobe.sort());

        const packageChangeComponent = () => {
            return (
                <PackageWarning
                    description={MESSAGES.MODAL.ADD_SKU.CONFIRM.DESCRIPTION}
                    handleOnChange={this.updateValidation}
                    confirmSelection={confirmSelection}
                    configLicenseName={configLicenseName}
                    skuLicenseName={configurationLicensesTobe.join(', ')}
                />
            );
        };

        try {
            isLoading(true);
            const addSkuValidation = await this.checkAddSkuValidation(selectedSkus);
            if (addSkuValidation) {
                const paramsToCheck = {
                    configurationId,
                    params: { ids: selectedSkuIds.join(',') },
                };
                const { availableSkus, duplicateSkus } =
                    (configurationType === CONFIGURATION_TYPE.MASTER ||
                        configurationType === CONFIGURATION_TYPE.ALTERNATE) &&
                    (await fetchSkusByConfigurationId(paramsToCheck));

                if (duplicateSkus) {
                    const onAddDuplicate = () => {
                        this.setState({
                            workflowStep: {
                                id: 'step_2',
                                hasPackageChange,
                                skuLicenseName: configurationLicensesTobe.join(', '),
                                handleOnConfirmSelection: () => {
                                    callFunc(onAddSku, selectedSkus);
                                },
                            },
                            validation: {
                                confirmSelection: !hasPackageChange,
                            },
                        });
                    };
                    openModal(MODALS.DUPLICATE_SKUS, {
                        duplicateSkus: this.rearrangeSkus(duplicateSkus),
                        packageChangeComponent,
                        hasPackageChange,
                        availableSkus,
                        onAddSku: onAddDuplicate,
                        selectedSkus,
                    });
                } else {
                    this.setState({
                        workflowStep: {
                            id: 'step_2',
                            hasPackageChange,
                            skuLicenseName: configurationLicensesTobe.join(', '),
                            handleOnConfirmSelection: () => {
                                callFunc(onAddSku, selectedSkus);
                            },
                        },
                        validation: {
                            confirmSelection: !hasPackageChange,
                        },
                    });
                }
            }
        } catch (e) {
            isLoading(false);
            openModal(MODALS.COMMON, {
                title: MESSAGES.MODAL.CONFIRM.TITLE.ERROR,
                description: extractErrorMessage(e),
            });
        } finally {
            isLoading(false);
        }
    };

    handleSKUSelection = sku => {
        const { newAddedSkuIds, newAddedSkus, selectedSkuIds } = this.state;

        // Remove the SKU from the list if already available and add it in the list if not-
        if (selectedSkuIds.indexOf(sku.id) === -1) {
            this.setState(
                {
                    selectedSkuIds: [...selectedSkuIds, sku.id],
                    newAddedSkuIds: [...newAddedSkuIds, sku.id],
                    newAddedSkus: [...newAddedSkus, sku],
                },
                () => {
                    const { selectedSkuIds, skusList } = this.state;
                    this.setState({
                        selectAll: selectedSkuIds.length === skusList.length,
                    });
                }
            );
        } else {
            this.setState(
                {
                    selectedSkuIds: selectedSkuIds.filter(id => id !== sku.id),
                    newAddedSkuIds: newAddedSkuIds.filter(id => id !== sku.id),
                    newAddedSkus: [...newAddedSkus, sku],
                },
                () => {
                    const { selectedSkuIds, skusList } = this.state;
                    this.setState({
                        selectAll: selectedSkuIds.length === skusList.length,
                    });
                }
            );
        }
    };

    getListViewSchema = () => {
        const { modal } = this.props;
        const schema = _cloneDeep(AddSkusPackageListSchema);
        const configurationType = _get(modal, 'data.configurationType');

        if (configurationType !== CONFIGURATION_TYPE.ADD_ON) {
            schema.columns = schema.columns.filter(column => column.id !== 'sponsorBy');
        }

        return schema;
    };

    onChangeSKUSponsor = (skuId, sponsor) => {
        const { label, value, licenses } = sponsor || {};
        this.setState(prevState => ({
            skusList: prevState.skusList.map(sku => {
                if (sku.id === skuId) {
                    return {
                        ...sku,
                        sponsor: label,
                        sponsorBy: value,
                        sponsorLicenses: licenses?.map(({ licenseId }) => licenseId),
                    };
                }
                return sku;
            }),
        }));
    };

    renderContent = () => {
        const { modal } = this.props;
        const configOwnerLicensesIds = _get(modal, 'data.ownerLicensesIds');
        const configLicenseName = _get(modal, 'data.configLicensesName');
        const {
            selectedSkuIds,
            skusList,
            workflowStep,
            validation: { confirmSelection } = {},
        } = this.state;
        const cellTypes = _get(modal, 'data.cellTypes', {});
        const totalRecords = skusList.length;
        const totalSelectedSkuIds = selectedSkuIds.length;
        const sortedSkuData = sortSkuList(skusList);
        const addPackageData = data => {
            return data.map(pack => {
                let ownerLicenses;
                if (pack?.sponsorBy) {
                    ownerLicenses = pack?.sponsorLicenses || [];
                } else {
                    ownerLicenses = configOwnerLicensesIds;
                }
                return {
                    ...pack,
                    licenses: generateSKULicenseName(pack?.licenses, ownerLicenses),
                };
            });
        };
        if (workflowStep?.id === 'step_2') {
            if (!workflowStep.hasPackageChange) {
                return (
                    <Row margin="0 1rem">
                        <AlertIcon style={{ width: 45, height: 'auto', color: COLORS.ERROR }} />
                        <Typography lineHeight="3" gutterLeft="1rem">
                            {MESSAGES.MODAL.ADD_SKU.CONFIRM.DESCRIPTION}
                        </Typography>
                    </Row>
                );
            }
            return (
                <PackageWarning
                    description={MESSAGES.MODAL.ADD_SKU.CONFIRM.DESCRIPTION}
                    handleOnChange={this.updateValidation}
                    confirmSelection={confirmSelection}
                    configLicenseName={configLicenseName}
                    skuLicenseName={workflowStep.skuLicenseName}
                />
            );
        }
        const configurationType = _get(modal, 'data.configurationType');
        const renderSelectAll =
            configurationType === CONFIGURATION_TYPE.ADD_ON ? this.renderSelectAllCheckbox : null;

        return (
            <AddSkusModalStyled>
                <Row direction="row-reverse" alignItems="flex-start" margin="10px">
                    <Typography type="label" fontSize="18px" gutterTop="5">
                        {totalSelectedSkuIds} RECORDS SELECTED
                    </Typography>
                </Row>
                <ListView
                    cellTypes={cellTypes}
                    data={addPackageData(filterSystemSkusFromList(sortedSkuData))}
                    handlers={{
                        onChangeSKUSponsor: this.onChangeSKUSponsor,
                        removeSku: this.handleRemoveSku,
                    }}
                    schema={this.getListViewSchema()}
                    selectable={this.renderSelectable}
                    renderSelectAll={renderSelectAll}
                />
                <Row margin="10px">
                    <Typography type="paginationLabel" fontSize="18px" gutterTop="5">
                        {totalRecords} Total Records
                    </Typography>
                </Row>
            </AddSkusModalStyled>
        );
    };

    handleOnChangeSelectAll = async () => {
        const { selectAll, skusList, saveSelectedSkusId } = this.state;

        const skuIds = skusList.map(sku => sku.id);

        await this.setState({ selectAll: !selectAll });

        if (this.state.selectAll) {
            const newAddedFilteredIds = skuIds.filter(id => !saveSelectedSkusId.includes(id));
            const newAddedFilteredIdsList = skusList.filter(
                sku => !saveSelectedSkusId.includes(sku.id)
            );

            this.setState({
                selectedSkuIds: [...skuIds],
                newAddedSkuIds: [...newAddedFilteredIds],
                newAddedSkus: [...newAddedFilteredIdsList],
            });
        } else {
            this.setState({
                selectedSkuIds: [...saveSelectedSkusId],
                newAddedSkuIds: [],
                newAddedSkus: [],
            });
        }
    };

    renderSelectAllCheckbox = () => {
        return (
            <Checkbox
                checked={this.state.selectAll}
                type="checkbox"
                isDisabled={false}
                icon={CheckboxEmptyIcon}
                checkedIcon={CheckboxFilledIcon}
                onChange={this.handleOnChangeSelectAll}
            />
        );
    };

    renderSelectable = sku => {
        const { selectedSkuIds } = this.state;
        const onClickHandler = () => {
            this.handleSKUSelection(sku);
        };

        return (
            <Checkbox
                checked={selectedSkuIds.indexOf(sku.id) !== -1}
                type="checkbox"
                isDisabled={sku.isActive === 0 || sku.disabled}
                icon={CheckboxEmptyIcon}
                checkedIcon={CheckboxFilledIcon}
                onChange={onClickHandler}
            />
        );
    };

    render() {
        return <Modal {...this.getModalProps()} />;
    }
}

const mapStateToProps = createStructuredSelector({
    modal: uiSelectors.getModalById(MODALS.ADD_SKUS_PACKAGING),
});

const mapDispatchToProps = {
    closeModal: uiActions.closeModal,
    fetchMasterConfigSkusForPartner: adminActions.fetchMasterConfigSkusForPartner,
    fetchSkusByConfigurationId: adminActions.fetchSkusByConfigurationId,
    fetchCompany: adminActions.fetchCompany,
    fetchPartner: adminActions.fetchPartner,
    isLoading: uiActions.isLoading,
    openModal: uiActions.openModal,
};

export { AddSkusModalPackaging as AddSkusModalPackagingUnWrapped };
export default connect(mapStateToProps, mapDispatchToProps)(AddSkusModalPackaging);
